import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";


// ==========================================
// NUMBER TO WORDS
// ==========================================

const numberToWords = (number) => {
  number = Number(number);

  if (!Number.isFinite(number)) {
    return "";
  }

  if (number === 0) {
    return "Zero";
  }

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convert = (num) => {

    if (num < 20) {
      return ones[num];
    }

    if (num < 100) {
      return (
        tens[Math.floor(num / 10)] +
        (num % 10 ? `-${ones[num % 10]}` : "")
      );
    }

    if (num < 1000) {
      return (
        `${ones[Math.floor(num / 100)]} Hundred` +
        (num % 100
          ? ` And ${convert(num % 100)}`
          : "")
      );
    }

    if (num < 100000) {
      return (
        `${convert(Math.floor(num / 1000))} Thousand` +
        (num % 1000
          ? ` ${convert(num % 1000)}`
          : "")
      );
    }

    if (num < 10000000) {
      return (
        `${convert(Math.floor(num / 100000))} Lakh` +
        (num % 100000
          ? ` ${convert(num % 100000)}`
          : "")
      );
    }

    return (
      `${convert(Math.floor(num / 10000000))} Crore` +
      (num % 10000000
        ? ` ${convert(num % 10000000)}`
        : "")
    );
  };

  return convert(Math.floor(number));
};


// ==========================================
// DATE
// ==========================================

const formatDate = (date) => {

  if (!date) {
    return "";
  }

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString("en-GB");
};


// ==========================================
// SINGLE RECEIPT
// ==========================================

const ReceiptBlock = ({
  expense,
  institute,
}) => {

  const amount = Number(
    expense?.amount || 0
  );

  const amountInWords =
    numberToWords(amount);


  return (

    <View style={styles.receipt}>

      {/* ======================================
          INSTITUTE HEADER
      ====================================== */}

      <View style={styles.instituteSection}>

        <Text style={styles.instituteName}>
          {institute?.instituteName ||
            ""}
        </Text>

        <Text style={styles.address}>
          {institute?.instituteAddress ||
            ""}
        </Text>

      </View>


      {/* ======================================
          RECEIPT TOP ROW
      ====================================== */}

      <View style={styles.topRow}>

        <Text style={styles.receiptNumber}>
          No:{" "}
          {expense?.receiptNo || "-"}/{expense?.session}
        </Text>


        <Text style={styles.voucher}>
          VOUCHER
        </Text>


        <Text style={styles.date}>
          Date:{" "}
          {formatDate(expense?.date)}
        </Text>

      </View>


      {/* ======================================
          RECEIVED FROM
      ====================================== */}

      <View style={styles.receivedRow}>

        <Text style={styles.receivedLabel}>
          Received with thanks from Mr/ Mrs:
        </Text>


        <Text style={styles.receivedValue}>
          {expense?.name || "-"}
        </Text>

      </View>


      {/* ======================================
          AMOUNT IN WORDS
      ====================================== */}

      <View style={styles.wordsRow}>

        <Text style={styles.wordsLabel}>
          The Sum of Rupees in Words
        </Text>


        <View style={styles.wordsLine}>

          <Text style={styles.wordsValue}>
            {amountInWords}
          </Text>

        </View>

      </View>


      {/* ======================================
          TOWARDS
      ====================================== */}

      <View style={styles.towardsRow}>

        <Text style={styles.towardsLabel}>
          towards
        </Text>


        <View style={styles.towardsLine}>

          <Text style={styles.towardsValue}>
            {expense?.towards || "-"}
          </Text>

        </View>

      </View>


      {/* ======================================
          PAYMENT MODE
      ====================================== */}

      <View style={styles.paymentRow}>

        <Text style={styles.paymentText}>
          Mode of Payment:{" "}
          {expense?.paymentMode || "-"}
        </Text>

      </View>



      <View style={styles.bottomRow}>

        <View style={styles.amountBox}>

          <Text style={styles.amountText}>
            Rs.{" "}
            {amount.toLocaleString("en-IN")}
          </Text>

          <View style={styles.amountLine} />

        </View>


        <View style={styles.signatureBox}>

          <Text style={styles.signatureText}>
            Receiver's Signature
          </Text>

        </View>

      </View>

    </View>

  );
};


// ==========================================
// MAIN EXPENSE RECEIPT
// ==========================================

const ExpenseReceipt = ({
  expense,
  institute,
}) => {

  if (!expense) {
    return null;
  }


  return (

    <Document>

      <Page
        size="A4"
        style={styles.page}
      >

        {/* FIRST COPY */}

        <ReceiptBlock
          expense={expense}
          institute={institute}
        />


        {/* CUT / SEPARATOR */}

        <View style={styles.separator}>

          <Text style={styles.separatorText}>
            ✂
          </Text>

        </View>


        {/* SECOND COPY */}

        <ReceiptBlock
          expense={expense}
          institute={institute}
        />

      </Page>

    </Document>

  );
};


// ==========================================
// STYLES
// ==========================================

const styles = StyleSheet.create({

  page: {
    padding: 25,
    fontSize: 10,
    fontFamily: "Helvetica",
  },


  // ========================================
  // RECEIPT CONTAINER
  // ========================================

  receipt: {
    height: 365,
    borderWidth: 1,
    borderColor: "#222",
    padding: 12,
    flexDirection: "column",
  },


  // ========================================
  // INSTITUTE
  // ========================================

  instituteSection: {
    alignItems: "center",
    borderBottomWidth: 0.5,
    borderBottomColor: "#555",
    paddingBottom: 8,
  },


  instituteName: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },


  address: {
    fontSize: 10,
    marginTop: 6,
    textAlign: "center",
  },


  // ========================================
  // TOP ROW
  // ========================================

  topRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 10,
    marginBottom: 12,
  },


  receiptNumber: {
    width: "33%",
    fontSize: 11,
    fontWeight: "bold",
  },


  voucher: {
    width: "34%",
    textAlign: "center",
    fontSize: 14,
    fontWeight: "bold",
  },


  date: {
    width: "33%",
    textAlign: "right",
    fontSize: 11,
    fontWeight: "bold",
  },


  // ========================================
  // RECEIVED FROM
  // ========================================

  receivedRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginTop: 3,
  },


  receivedLabel: {
    fontSize: 10.5,
    fontWeight: "bold",
    width: "38%",
  },


  receivedValue: {
    fontSize: 11,
    fontWeight: "bold",
    width: "62%",
    borderBottomWidth: 0.8,
    borderBottomColor: "#222",
    paddingBottom: 4,
  },


  // ========================================
  // WORDS
  // ========================================

  wordsRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginTop: 20,
  },


  wordsLabel: {
    width: "31%",
    fontSize: 10.5,
    fontWeight: "bold",
  },


  wordsLine: {
    width: "69%",
    borderBottomWidth: 0.8,
    borderBottomColor: "#222",
    paddingBottom: 4,
  },


  wordsValue: {
    fontSize: 10.5,
    fontWeight: "bold",
  },


  // ========================================
  // TOWARDS
  // ========================================

  towardsRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    marginTop: 18,
  },


  towardsLabel: {
    width: "12%",
    fontSize: 10.5,
    fontWeight: "bold",
  },


  towardsLine: {
    width: "88%",
    borderBottomWidth: 0.8,
    borderBottomColor: "#222",
    paddingBottom: 4,
  },


  towardsValue: {
    fontSize: 10.5,
    fontWeight: "bold",
  },


  // ========================================
  // PAYMENT MODE
  // ========================================

  paymentRow: {
    alignItems: "flex-end",
    marginTop: 55,
  },


  paymentText: {
    fontSize: 10.5,
    fontWeight: "bold",
  },


  // ========================================
  // BOTTOM
  // ========================================

  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 38,
  },


  amountBox: {
    width: "30%",
  },


  amountText: {
    fontSize: 11,
    fontWeight: "bold",
    paddingLeft: 5,
  },


  amountLine: {
    borderBottomWidth: 0.8,
    borderBottomColor: "#222",
    marginTop: 1,
  },


  signatureBox: {
    width: "35%",
    alignItems: "center",
  },


  signatureText: {
    fontSize: 10.5,
    fontWeight: "bold",
  },


  // ========================================
  // SEPARATOR
  // ========================================

  separator: {
    height: 25,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },


  separatorText: {
    fontSize: 10,
    color: "#777",
  },

});


export default ExpenseReceipt;